import React from 'react';

export interface ActivityEntry {
  readonly activityId: string;
  readonly actorName: string;
  readonly actionType: string;
  readonly targetName: string;
  readonly timestamp: string;
}

export interface ActivitySidebarProps {
  readonly entries: ActivityEntry[];
  readonly isLoading: boolean;
  readonly onClose?: () => void;
}

const ACTION_LABELS: Record<string, string> = {
  CardCreated: 'created',
  CardMoved: 'moved',
  CardArchived: 'archived',
  CardUpdated: 'updated',
  CommentAdded: 'commented on',
  MemberAssigned: 'assigned',
  ChecklistItemToggled: 'checked off item on',
  LabelCreated: 'added label to',
  AttachmentAdded: 'attached file to',
};

export function ActivitySidebar({ entries, isLoading, onClose }: ActivitySidebarProps): React.ReactElement {
  return (
    <aside aria-label="Activity feed" className="p-4 bg-card text-card-foreground rounded-lg">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Activity</h3>
        {onClose && (
          <button
            onClick={onClose}
            aria-label="Close activity"
            className="p-1 rounded hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring"
          >
            ✕
          </button>
        )}
      </div>

      {isLoading && <p className="text-sm text-muted-foreground">Loading activity...</p>}
      {!isLoading && entries.length === 0 && <p className="text-sm text-muted-foreground">No activity yet.</p>}

      <ul className="space-y-3">
        {entries.map((entry) => (
          <li key={entry.activityId} className="text-sm rounded-md p-2 hover:bg-accent/50 transition-colors">
            <span className="font-medium text-foreground">{entry.actorName}</span>{' '}
            <span className="text-muted-foreground">{ACTION_LABELS[entry.actionType] ?? entry.actionType}</span>{' '}
            <span className="font-medium text-foreground">{entry.targetName}</span>
            <time dateTime={entry.timestamp} className="block text-xs text-muted-foreground mt-0.5" role="time">
              {new Date(entry.timestamp).toLocaleString()}
            </time>
          </li>
        ))}
      </ul>
    </aside>
  );
}
